/**
 * FiscalAgentDownloads — instalatory usługi fiskalnej i instrukcja jej uruchomienia.
 *
 * Mieszka pod listą stanowisk, bo to jest moment, w którym człowiek naprawdę tego potrzebuje:
 * dodał stanowisko, dostał token i musi teraz postawić usługę na komputerze przy drukarce.
 *
 * DLACZEGO INSTRUKCJA JEST TU, A NIE W OSOBNYM DOKUMENCIE: usługę stawia się raz, zwykle
 * ktoś, kto nie robił tego nigdy. Odesłanie do pliku gdzieś indziej znaczy, że połowa osób
 * zadzwoni po wsparcie — kroki muszą być na tym samym ekranie co token i pobieranie.
 *
 * Gdy wydania jeszcze nie ma, mówimy to WPROST zamiast pokazywać martwy przycisk. Instalator
 * Windows powstaje przez kompilację na maszynie z Windows, więc bywa, że jest tylko macOS.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Apple, Download, MonitorDown } from 'lucide-react';

import { Button } from '@/components/Button';
import { useToast } from '@/contexts/ToastContext';
import {
  downloadAgentRelease,
  type FiscalAgentRelease,
  listAgentReleases,
} from '@/lib/fiscal/fiscal-api';

function formatSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function FiscalAgentDownloads({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  const releasesQuery = useQuery({
    queryKey: ['fiscal', 'agent-releases', companyId],
    queryFn: () => listAgentReleases(companyId),
  });

  const releases = releasesQuery.data ?? [];
  const byPlatform = (platform: FiscalAgentRelease['platform']) =>
    releases.find((r) => r.platform === platform);

  async function handleDownload(release: FiscalAgentRelease) {
    setBusyId(release.id);
    try {
      await downloadAgentRelease(release, companyId);
    } catch {
      toast.show({
        variant: 'error',
        title: t('tenant.handelMagazyn.agent.downloadFailed', 'Nie udało się pobrać instalatora'),
      });
    } finally {
      setBusyId(null);
    }
  }

  function renderPlatform(
    platform: FiscalAgentRelease['platform'],
    label: string,
    Icon: typeof MonitorDown,
  ) {
    const release = byPlatform(platform);
    return (
      <div
        className="flex items-center justify-between gap-3 rounded border border-border-primary px-3 py-2"
        data-testid={`hm-agent-${platform}`}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
            <Icon className="h-4 w-4 shrink-0" />
            {label}
          </div>
          {release ? (
            <div className="mt-0.5 truncate text-xs text-text-secondary">
              {t('tenant.handelMagazyn.agent.version', {
                defaultValue: 'Wersja {{version}}',
                version: release.version,
              })}
              {' · '}
              {formatSize(release.sizeBytes)}
              {release.notes ? ` · ${release.notes}` : ''}
            </div>
          ) : (
            <div className="mt-0.5 text-xs text-text-secondary">
              {t(
                'tenant.handelMagazyn.agent.notAvailable',
                'Instalator dla tego systemu nie został jeszcze udostępniony.',
              )}
            </div>
          )}
        </div>
        {release && (
          <Button
            variant="secondary"
            size="sm"
            disabled={busyId === release.id}
            onClick={() => void handleDownload(release)}
            data-testid={`hm-agent-download-${platform}`}
          >
            <Download className="mr-1.5 h-4 w-4" />
            {busyId === release.id
              ? t('tenant.handelMagazyn.agent.downloading', 'Pobieram…')
              : t('tenant.handelMagazyn.agent.download', 'Pobierz')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <section className="mt-6" data-testid="hm-agent-section">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">
          {t('tenant.handelMagazyn.agent.title', 'Usługa na stanowisku')}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowGuide((v) => !v)}
          data-testid="hm-agent-guide-toggle"
        >
          {showGuide
            ? t('tenant.handelMagazyn.agent.hideGuide', 'Ukryj instrukcję')
            : t('tenant.handelMagazyn.agent.showGuide', 'Pokaż instrukcję')}
        </Button>
      </div>

      <p className="mb-2 text-xs text-text-secondary">
        {t(
          'tenant.handelMagazyn.agent.intro',
          'Paragon drukuje usługa zainstalowana na komputerze, do którego podłączona jest drukarka. Panel sam do drukarki nie sięga.',
        )}
      </p>

      {releasesQuery.isLoading ? (
        <p className="text-sm text-text-secondary">{t('common.loading')}</p>
      ) : (
        <div className="space-y-2">
          {renderPlatform(
            'windows',
            t('tenant.handelMagazyn.agent.windows', 'Windows 10, 11, Server'),
            MonitorDown,
          )}
          {renderPlatform('macos', t('tenant.handelMagazyn.agent.macos', 'macOS'), Apple)}
        </div>
      )}

      {showGuide && <InstallGuide />}
    </section>
  );
}

/**
 * Instrukcja instalacji. Kolejność kroków nie jest przypadkowa — token powstaje przy zakładaniu
 * stanowiska i pokazuje się RAZ, więc musi być zdobyty zanim ktokolwiek uruchomi instalator.
 */
function InstallGuide() {
  const { t } = useTranslation();

  const steps: Array<{ key: string; fallback: string }> = [
    {
      key: 'tenant.handelMagazyn.agent.guide.step1',
      fallback:
        'Dodaj stanowisko na tej stronie i ZAPISZ token, który pokaże się raz. Później nie da się go odczytać — trzeba wygenerować nowy.',
    },
    {
      key: 'tenant.handelMagazyn.agent.guide.step2',
      fallback:
        'Podłącz drukarkę do komputera: kablem sieciowym (wtedy potrzebny jest jej adres IP i port) albo szeregowym (wtedy nazwa portu COM).',
    },
    {
      key: 'tenant.handelMagazyn.agent.guide.step3',
      fallback:
        'Uruchom instalator na tym komputerze i podaj w kreatorze: adres panelu, token stanowiska, sposób połączenia z drukarką oraz jej stronę kodową.',
    },
    {
      key: 'tenant.handelMagazyn.agent.guide.step4',
      fallback:
        'Usługa startuje razem z systemem i sama zgłasza się do panelu. Gdy działa, stanowisko na liście powyżej przestaje być opisane jako „usługa nie odpowiada".',
    },
    {
      key: 'tenant.handelMagazyn.agent.guide.step5',
      fallback:
        'Przypisz stawki VAT do liter drukarki i formy płatności do typów płatności. Bez tego druk się nie uda i powie, czego brakuje — to celowe, bo zgadnięta stawka oznacza sprzedaż zaksięgowaną błędnie.',
    },
  ];

  return (
    <div
      className="mt-3 rounded border border-border-primary bg-bg-secondary px-3 py-3"
      data-testid="hm-agent-guide"
    >
      <h3 className="mb-2 text-sm font-semibold text-text-primary">
        {t('tenant.handelMagazyn.agent.guide.title', 'Jak uruchomić usługę')}
      </h3>
      <ol className="list-decimal space-y-1.5 pl-5 text-xs text-text-secondary">
        {steps.map((step) => (
          <li key={step.key}>{t(step.key, step.fallback)}</li>
        ))}
      </ol>

      <h3 className="mb-1 mt-3 text-sm font-semibold text-text-primary">
        {t('tenant.handelMagazyn.agent.guide.troubleTitle', 'Gdy coś nie działa')}
      </h3>
      <ul className="list-disc space-y-1.5 pl-5 text-xs text-text-secondary">
        <li>
          {t(
            'tenant.handelMagazyn.agent.guide.trouble1',
            'Stanowisko opisane jako „usługa nie odpowiada": usługa jest zatrzymana albo komputer nie ma dostępu do internetu. Sprawdź, czy usługa jest uruchomiona.',
          )}
        </li>
        <li>
          {t(
            'tenant.handelMagazyn.agent.guide.trouble2',
            'Drukarka odmawia z komunikatem o menu lokalnym: ktoś wszedł w menu na urządzeniu. Wyjdź z menu i powtórz wydruk.',
          )}
        </li>
        <li>
          {t(
            'tenant.handelMagazyn.agent.guide.trouble3',
            'Komunikat o zaległym raporcie dobowym: wykonaj raport dobowy na drukarce, inaczej nie przyjmie sprzedaży.',
          )}
        </li>
        <li>
          {t(
            'tenant.handelMagazyn.agent.guide.trouble4',
            'Jedna drukarka podłączona po sieci obsługuje wiele komputerów — usługę stawia się wtedy TYLKO na jednym z nich. Druga usługa wskazująca tę samą drukarkę zostanie odrzucona.',
          )}
        </li>
      </ul>
    </div>
  );
}
